import { useContext, createContext, useEffect, useState, useCallback } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { auth, db, functions } from "../firebase";
import { priceIfIJoin } from "../utils/pricing";
import { syncPublicProfile, getPublicTeachers } from "../utils/publicProfile";
import { TERMS_VERSION } from "../legal/config";
import { useLang } from "../i18n/LanguageContext";

const AuthContext = createContext();

/**
 * Appelle une fonction du serveur. Les erreurs gardent le message français du
 * serveur (traduit à l'affichage) et, si besoin, les montants à formater.
 */
export const callServer = async (name, data) => {
  try {
    const res = await httpsCallable(functions, name)(data);
    return res.data;
  } catch (e) {
    const err = new Error(e?.message || "Une erreur est survenue. Veuillez réessayer.");
    err.code = e?.code;
    if (e?.details?.i18nKey) {
      err.i18nKey = e.details.i18nKey;
      err.i18nAmounts = e.details.i18nAmounts;
    }
    // Serveur injoignable (fonctions pas encore déployées, coupure réseau…)
    if (["functions/internal", "functions/unavailable"].includes(e?.code) && /^(internal|unavailable)$/i.test(e?.message || "")) {
      err.message = "Le serveur Tatie Soso ne répond pas. Réessayez dans un instant.";
    }
    throw err;
  }
};

// Prix partagé entre les inscrits (voir utils/pricing.js)
export const computeCharge = (course) => {
  const totalCharge = priceIfIJoin(course);
  return { pricePerStudent: totalCharge, fee: 0, totalCharge };
};

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang } = useLang();

  // Les e-mails envoyés par Firebase (confirmation, mot de passe oublié)
  // partent dans la langue choisie sur le site : FR, NL ou EN.
  useEffect(() => {
    auth.languageCode = lang;
  }, [lang]);

  // Fusionne l'utilisateur Firebase avec son profil Firestore
  const loadUser = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setUser(null);
      return null;
    }
    const snap = await getDoc(doc(db, "users", firebaseUser.uid));
    const merged = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      emailVerified: firebaseUser.emailVerified,
      providerId: firebaseUser.providerData?.[0]?.providerId || "",
      hasProfile: snap.exists(),
      ...(snap.exists() ? snap.data() : {}),
    };
    setUser(merged);
    // Fiche publique des profs (nom, photo, description) tenue à jour
    if (snap.exists()) {
      syncPublicProfile(firebaseUser.uid, snap.data()).catch((e) =>
        console.warn("Fiche publique non synchronisée :", e.code || e.message)
      );
    }
    return merged;
  }, []);

  // Recharge le profil (après inscription, modification du solde, etc.)
  const refreshUser = useCallback(() => loadUser(auth.currentUser), [loadUser]);

  // --- Auth ---
  const googleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    return signInWithPopup(auth, provider);
  };

  // Inscription / connexion par e-mail et mot de passe (sans Google)
  const emailSignUp = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
    if (displayName) await updateProfile(cred.user, { displayName }).catch(() => {});
    await sendEmailVerification(cred.user).catch((e) => console.warn("E-mail de confirmation :", e.code));
    return cred;
  };

  const emailSignIn = (email, password) => signInWithEmailAndPassword(auth, email.trim(), password);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email.trim());

  const resendVerification = async () => {
    if (auth.currentUser) await sendEmailVerification(auth.currentUser);
  };

  /** Après avoir cliqué sur le lien reçu : recharge l'état « e-mail confirmé » */
  const reloadVerification = async () => {
    if (!auth.currentUser) return false;
    await auth.currentUser.reload();
    await auth.currentUser.getIdToken(true); // jeton à jour pour le serveur
    await loadUser(auth.currentUser);
    return auth.currentUser.emailVerified;
  };

  const logOut = () => signOut(auth);

  // --- Profil utilisateur ---
  /**
   * Crée le profil Firestore si besoin.
   * Retourne "No user found." en mode connexion quand aucun profil n'existe.
   */
  const createUserProfileDocument = async (info, isSignIn) => {
    if (!info.User) return "no user";
    const userRef = doc(db, "users", info.User.uid);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists() && isSignIn) {
      return "No user found.";
    }

    if (!snapshot.exists()) {
      const { displayName, email } = info.User;
      await setDoc(userRef, {
        firstName: info.FirstName,
        lastName: info.LastName,
        displayName: displayName || `${info.FirstName} ${info.LastName}`,
        email,
        dateOfBirth: info.DateOfBirth,
        role: info.Role,
        balance: 0,
        createdAt: new Date().toISOString(),
        // Preuve de l'acceptation des CGV / de la politique de confidentialité
        termsAcceptedAt: new Date().toISOString(),
        termsVersion: TERMS_VERSION,
        // Mineurs : accord du parent / tuteur déclaré à l'inscription
        ...(info.ParentalConsent ? { parentalConsent: info.ParentalConsent } : {}),
      });
    }
    return "ok";
  };

  const fetchUserProfile = async () => {
    if (!user) return null;
    const snap = await getDoc(doc(db, "users", user.uid));
    return snap.exists() ? snap.data() : null;
  };

  const updateUserProfile = async (dateOfBirth, description, firstName, lastName) => {
    if (!user) return;
    await updateDoc(doc(db, "users", user.uid), { dateOfBirth, firstName, lastName, description });
    await refreshUser();
  };

  const updateUserProfilePicture = async (profilePicture) => {
    if (!user) return;
    await updateDoc(doc(db, "users", user.uid), { profilePicture });
    await refreshUser();
  };

  // --- Mode élève / prof ---
  // role : 0 = admin, 1 = peut enseigner, 2 = élève uniquement
  // mode : préférence d'affichage pour ceux qui peuvent enseigner ("prof" | "eleve")
  const currentMode = user?.role === 1 ? user.mode || "prof" : user?.role === 0 ? "admin" : "eleve";

  /** Passe en mode "prof" ou "eleve". Un élève qui passe en prof devient aussi enseignant. */
  const switchMode = async (mode) => {
    if (!user || user.role === 0) return;
    const ref = doc(db, "users", user.uid);
    if (mode === "prof" && user.role === 2) {
      await updateDoc(ref, { role: 1, mode: "prof" });
    } else {
      await updateDoc(ref, { mode });
    }
    await refreshUser();
  };

  // --- Cours ---
  const getAllCourses = async () => {
    const snapshot = await getDocs(collection(db, "courses"));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  // Fiches publiques des profs (id = uid), lisibles même sans être connecté
  const getAllProfs = () => getPublicTeachers();

  // Mes inscriptions (le paramètre e-mail est gardé pour compatibilité, on cherche par uid)
  const getAllCoursStudentByStudentMail = async () => {
    if (!auth.currentUser) return [];
    const q = query(collection(db, "coursStudent"), where("studentUid", "==", auth.currentUser.uid));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  /**
   * Crée le cours. Les infos de connexion vidéo (salle Jitsi ou lien perso)
   * sont rangées à part dans `courseSessions/{id}` pour pouvoir être
   * réservées aux inscrits et au prof via les règles Firestore.
   */
  const createCourseDocument = async (course) => {
    const courseData = {
      id: course.id,
      name: course.name,
      subject: course.subject || "",
      ownerId: auth.currentUser.uid,
      date: course.date,
      time: course.time || "",
      duration: Number(course.duration) || 60,
      videoType: course.videoType || "jitsi",
      price: Number(course.price),
      maxStudents: Number(course.maxStudents),
      description: course.description,
      students: 0,
      createdAt: new Date().toISOString(),
    };
    await setDoc(doc(db, "courses", course.id), courseData);
    await setDoc(doc(db, "coursesHistory", course.id), courseData).catch(() => {});
    // Salle vidéo rangée à part : visible seulement par le prof et les inscrits
    await setDoc(doc(db, "courseSessions", course.id), {
      courseId: course.id,
      ownerId: auth.currentUser.uid,
      videoType: course.videoType || "jitsi",
      jitsiRoom: course.jitsiRoom || "",
      meetLink: course.meetLink || "",
    });
  };

  /** Infos de la salle vidéo d'un cours (null si inaccessible) */
  const getCourseSession = async (course) => {
    try {
      const snap = await getDoc(doc(db, "courseSessions", course.id));
      if (snap.exists()) return snap.data();
    } catch (e) {
      // pas inscrit (règles Firestore) ou erreur réseau
    }
    // Anciens cours (ou repli) : infos stockées directement dans le cours
    if (course.meetLink) return { videoType: "custom", meetLink: course.meetLink };
    if (course.jitsiRoom) return { videoType: "jitsi", jitsiRoom: course.jitsiRoom };
    return null;
  };

  // --- Argent : tout passe par le serveur (dossier functions/) ---
  // Le navigateur n'a pas le droit de modifier un solde (voir firestore.rules).

  /** Annule un cours : les élèves inscrits sont remboursés, puis le cours est supprimé. */
  const deleteCourse = async (courseId) => {
    await callServer("cancelCourse", { courseId, mode: "delete" });
  };

  /** Rembourse les élèves en gardant le cours (administration). Retourne le nombre d'élèves. */
  const refundCourseStudents = async (courseId) => {
    const r = await callServer("cancelCourse", { courseId, mode: "refund" });
    return r.refunded;
  };

  /**
   * Inscription avec le prix partagé : le nouveau venu paie prix / (inscrits + 1)
   * et les élèves déjà inscrits sont remboursés de la différence (calcul serveur).
   */
  const joinCourse = async (course) => {
    if (!user) throw new Error("Vous devez être connecté.");
    const r = await callServer("joinCourse", { courseId: course.id });
    await refreshUser();
    return r;
  };

  /** Après la fin du cours, le prof reçoit ce que les élèves ont payé − 10 %. */
  const claimCoursePayout = async (course) => {
    if (!user) throw new Error("Vous devez être connecté.");
    const r = await callServer("claimCoursePayout", { courseId: course.id });
    await refreshUser();
    return r;
  };

  /** Transfert de pièces vers un autre utilisateur (par e-mail) */
  const transferCoins = async (email, amount) => {
    const r = await callServer("transferCoins", { email, amount });
    await refreshUser();
    return r;
  };

  /** Dépôt PayPal : le serveur vérifie et encaisse la commande auprès de PayPal */
  const capturePaypalOrder = async (orderID) => {
    const r = await callServer("capturePaypalOrder", { orderID });
    await refreshUser();
    return r;
  };

  // --- Solde ---
  const fetchUserBalance = async () => {
    if (!user) return 0;
    const snap = await getDoc(doc(db, "users", user.uid));
    return snap.exists() ? Number(snap.data().balance) || 0 : 0;
  };

  // --- Auth listener ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        await loadUser(currentUser);
      } catch (e) {
        console.error("Erreur chargement du profil:", e);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [loadUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        googleSignIn,
        emailSignUp,
        emailSignIn,
        resetPassword,
        resendVerification,
        reloadVerification,
        logOut,
        refreshUser,
        currentMode,
        switchMode,
        createUserProfileDocument,
        fetchUserProfile,
        updateUserProfile,
        updateUserProfilePicture,
        getAllCourses,
        getAllProfs,
        getAllCoursStudentByStudentMail,
        createCourseDocument,
        getCourseSession,
        deleteCourse,
        refundCourseStudents,
        joinCourse,
        claimCoursePayout,
        transferCoins,
        capturePaypalOrder,
        fetchUserBalance,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const UserAuth = () => useContext(AuthContext);
